import { useEffect, useMemo, useState } from "react";
import { BreakPoints, useWindowDimensions } from "../../hooks/useWindowSize";
import { Pagination } from "../pagination/Pagination";

export type TablePageSize = {
  mobile: number;
  desktop: number;
};

export const DEFAULT_TABLE_PAGE_SIZE: TablePageSize = {
  mobile: 10,
  desktop: 25,
};

export type TablePaginationState = {
  pageIndex: number;
  pageCount: number;
  setPageIndex: (pageIndex: number) => void;
};

export type UseTablePaginationOptions = {
  pageSize?: TablePageSize;
  resetKey?: unknown;
};

const validPageSize = (pageSize: number) =>
  Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 1;

export const useTablePagination = <T,>(
  rows: readonly T[],
  {
    pageSize = DEFAULT_TABLE_PAGE_SIZE,
    resetKey,
  }: UseTablePaginationOptions = {}
) => {
  const isMobile = useWindowDimensions(BreakPoints.medium - 0.02);
  const activePageSize = validPageSize(
    isMobile ? pageSize.mobile : pageSize.desktop
  );
  const pageCount = Math.ceil(rows.length / activePageSize);
  const [pageIndex, setPageIndex] = useState(0);
  const lastPageIndex = Math.max(0, pageCount - 1);
  const visiblePageIndex = Math.min(pageIndex, lastPageIndex);

  useEffect(() => {
    setPageIndex(0);
  }, [activePageSize, resetKey]);

  useEffect(() => {
    setPageIndex((current) => Math.min(current, lastPageIndex));
  }, [lastPageIndex]);

  const pageRows = useMemo(() => {
    const start = visiblePageIndex * activePageSize;
    return rows.slice(start, start + activePageSize);
  }, [activePageSize, rows, visiblePageIndex]);

  return {
    pageRows,
    pageSize: activePageSize,
    pagination: {
      pageIndex: visiblePageIndex,
      pageCount,
      setPageIndex,
    } satisfies TablePaginationState,
  } as const;
};

export const TablePagination = ({
  pageIndex,
  pageCount,
  setPageIndex,
  className = "mt-2",
}: TablePaginationState & { className?: string }) => {
  if (pageCount <= 1) return null;

  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = pageIndex + 1 < pageCount;

  return (
    <Pagination
      className={className}
      pageInfo={{
        startCursor: hasPreviousPage ? String(pageIndex - 1) : null,
        endCursor: hasNextPage ? String(pageIndex + 1) : null,
        hasPreviousPage,
        hasNextPage,
      }}
      setBefore={(cursor) => setPageIndex(Number(cursor))}
      setAfter={(cursor) => setPageIndex(Number(cursor))}
    />
  );
};
